import React, { useEffect, useState } from 'react';

// Confetti component
const Confetti = ({ isActive }) => {
    const [particles, setParticles] = useState([]);

    useEffect(() => {
        if (!isActive) return;

        const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];
        const newParticles = [];

        for (let i = 0; i < 150; i++) {
            newParticles.push({
                id: i,
                x: Math.random() * 100,
                y: -10 - Math.random() * 20,
                color: colors[Math.floor(Math.random() * colors.length)],
                size: Math.random() * 8 + 4,
                speedY: Math.random() * 3 + 2,
                speedX: (Math.random() - 0.5) * 2,
                rotation: Math.random() * 360,
                rotationSpeed: (Math.random() - 0.5) * 10,
                opacity: 1,
                shape: Math.random() > 0.5 ? 'square' : 'circle'
            });
        }

        setParticles(newParticles);

        const interval = setInterval(() => {
            setParticles(prev =>
                prev.map(p => ({
                    ...p,
                    y: p.y + p.speedY,
                    x: p.x + p.speedX,
                    rotation: p.rotation + p.rotationSpeed,
                    opacity: p.y > 80 ? Math.max(0, p.opacity - 0.02) : p.opacity
                })).filter(p => p.opacity > 0 && p.y < 120)
            );
        }, 16);

        return () => clearInterval(interval);
    }, [isActive]);

    if (!isActive || particles.length === 0) return null;

    return (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
            {particles.map(p => (
                <div
                    key={p.id}
                    className="absolute"
                    style={{
                        left: `${p.x}%`,
                        top: `${p.y}%`,
                        width: p.size,
                        height: p.size,
                        backgroundColor: p.color,
                        borderRadius: p.shape === 'circle' ? '50%' : '2px',
                        transform: `rotate(${p.rotation}deg)`,
                        opacity: p.opacity,
                        transition: 'none'
                    }}
                />
            ))}
        </div>
    );
};

const PaymentSuccess = ({ bookingDetails, onClose }) => {
    const [showConfetti, setShowConfetti] = useState(true);

    // Handle ESC key press
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    // Stop confetti after 4 seconds
    useEffect(() => {
        const timer = setTimeout(() => setShowConfetti(false), 4000);
        return () => clearTimeout(timer);
    }, []);

    // Early return after hooks
    if (!bookingDetails) return null;

    const {
        id,
        service,
        selectedDate,
        selectedTime,
        razorpay_payment_id,
        phonepe_transaction_id
    } = bookingDetails;

    // Format date for display
    const formatDate = () => {
        const options = { weekday: 'short', month: 'short', day: 'numeric' };
        return selectedDate.toLocaleDateString('en-US', options);
    };

    const paymentId = phonepe_transaction_id || razorpay_payment_id;

    return (
        <>
            <Confetti isActive={showConfetti} />

            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">

                    {/* Success Header */}
                    <div className="px-8 pt-10 pb-6 text-center">
                        {/* Animated checkmark */}
                        <div className="mx-auto w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-500">
                            <svg
                                className="w-8 h-8 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                strokeWidth={3}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                    className="animate-in slide-in-from-left duration-500 delay-200"
                                />
                            </svg>
                        </div>

                        <h2 className="font-cormorant text-3xl font-bold text-gray-900 mb-2">
                            Booking Confirmed
                        </h2>
                        <p className="text-gray-500 text-sm">
                            We've sent the details to your email
                        </p>
                    </div>

                    {/* Booking Details */}
                    <div className="px-8 pb-6">
                        <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                            {/* Service & Time */}
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Session</p>
                                    <p className="font-medium text-gray-900">{service?.name}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Duration</p>
                                    <p className="font-medium text-gray-900">{service?.duration}</p>
                                </div>
                            </div>

                            {/* Date & Time */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">{formatDate()}</p>
                                        <p className="text-sm text-gray-500">{selectedTime}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-gray-900">{service?.price}</p>
                                </div>
                            </div>
                        </div>

                        {/* Reference IDs */}
                        <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                            <span>Booking #{id?.substring(0, 8).toUpperCase()}</span>
                            {paymentId && (
                                <span>Payment: {paymentId.substring(0, 12)}...</span>
                            )}
                        </div>
                    </div>

                    {/* What's Next */}
                    <div className="px-8 pb-6">
                        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-blue-900 mb-1">What's next?</p>
                                <p className="text-xs text-blue-700 leading-relaxed">
                                    Check your email for the calendar invite and session link. We'll send a reminder before your session.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="px-8 pb-8 space-y-3">
                        <button
                            onClick={onClose}
                            className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-medium text-sm hover:bg-gray-800 transition-colors"
                        >
                            Done
                        </button>

                        <div className="flex items-center justify-center gap-4 pt-2">
                            <a
                                href="mailto:ashimagoeltherapy@gmail.com?subject=Query about my booking"
                                className="text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1.5"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Email Support
                            </a>
                            <span className="text-gray-200">|</span>
                            <a
                                href="https://wa.me/919090199699?text=Hi, I have a query about my booking"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1.5"
                            >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                                WhatsApp
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PaymentSuccess;
